/**
 * Wire-Format-Konstanten für Längen-Validation und Krypto-Mindestanforderungen.
 *
 * Single Source of Truth für Frontend, Backend und Test-Fixtures.
 * Werte basieren auf AFO-002 (Audit-Empfehlung F8-Crypto): RSA-3072 als
 * Pilot-Mindestlevel, AES-256-GCM mit 96-Bit-IV.
 *
 * @see tickets/EPIC-001-patient-terminabsage/AFO-002-shared-vertrag/afo.md
 */

/**
 * Mindest-Modulus-Größe (Bit) für Tenant-RSA-Keys.
 * RSA-2048 wäre noch sicher, aber für den Pilot setzen wir 3072 als Mindestlevel
 * (Audit-Empfehlung F8-Crypto). Wird in DEV-006 (`spkiFingerprint`) zur
 * Validierung beim Re-Import des Public Keys wiederverwendet.
 */
export const MIN_TENANT_KEY_MODULUS_BITS = 3072 as const;

/**
 * Obergrenze der Public-Keys pro Tenant. Fachlich reichen zwei bis drei
 * (aktiver Key + Rotation); acht lassen Luft für Übergangsphasen. Die Grenze
 * greift VOR den SPKI-Imports: ohne sie erlaubt das 512-KiB-Body-Limit
 * mehrere hundert Keys, deren parallele WebCrypto-Prüfung pro Request
 * spürbar CPU bindet.
 */
export const MAX_TENANT_PUBLIC_KEYS = 8 as const;

/**
 * AES-GCM-IV: 12 Bytes = 96 Bit → 16 Base64-Zeichen (mit Padding-Konvention
 * gemäß Base64-Standard; 12 Bytes → 16 Zeichen exakt, kein '=' am Ende).
 */
export const IV_BASE64_LENGTH = 16 as const;

/**
 * RSA-3072-Wrap-Output: 384 Bytes → 512 Base64-Zeichen exakt.
 * Bei RSA-OAEP mit 3072-Bit-Modulus ist die Chiffrat-Länge gleich der
 * Modulus-Länge in Bytes (384). Base64 von 384 Bytes = 512 Zeichen.
 */
export const ENCRYPTED_KEY_BASE64_LENGTH = 512 as const;

/**
 * Mindestlänge des Payload-Chiffrats in Base64.
 * 24 Base64-Zeichen ≈ 18 Bytes Plain — kleinste plausible Payload-Größe
 * (auch leere Objekte plus AES-GCM-Tag liegen darüber).
 */
export const CIPHERTEXT_MIN_BASE64_LENGTH = 24 as const;

/**
 * Maximallänge des Payload-Chiffrats in Base64.
 * 4096 Base64-Zeichen ≈ 3 KiB Plain — deutlich über erwarteter
 * Payload-Größe (`{ dob, appointmentId, timeSlot, location, patientPublicKey }`),
 * schützt Backend gegen Heap-Flood-DoS.
 */
export const CIPHERTEXT_MAX_BASE64_LENGTH = 4096 as const;

/**
 * Regex für Standard-Base64-Format (nicht URL-safe).
 * Erlaubt: A-Z, a-z, 0-9, '+', '/' und am Ende 0–2 Padding-Zeichen '='.
 * Längen-Constraints werden separat geprüft, damit die Fehlermeldung
 * unterscheidbar bleibt (Format vs. Länge).
 */
export const BASE64_PATTERN = /^[A-Za-z0-9+/]*={0,2}$/;

/**
 * Regex für CSS-Hex-Farbwerte der Tenant-`themeColors`.
 * Erlaubt `#RGB`, `#RRGGBB` und `#RRGGBBAA` (mit Alpha), case-insensitive.
 * Andere CSS-Farbnotationen (rgb(), Farbnamen) sind im Branding-Editor
 * bewusst nicht zugelassen — ein einheitliches Hex-Format hält die
 * Live-Vorschau und den WCAG-Kontrast-Check simpel.
 */
export const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
